import * as React from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'

interface DialogContextValue {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Ties the panel to its `DialogTitle`, so the dialog is announced by name. */
  titleId: string
}

const DialogContext = React.createContext<DialogContextValue | null>(null)

/**
 * LIFO stack of currently-open dialogs' ESC handlers (#1216, raised
 * by Copilot review). Lets stacked dialogs (e.g. `Hints` renders an
 * Add/Edit dialog AND a Confirm dialog that can both be open at
 * once) close TOP-MOST FIRST on Escape rather than dismissing every
 * open dialog at once. Each open dialog pushes its handler on mount
 * and removes it on close / unmount; the single global keydown
 * listener invokes only the top of the stack and uses
 * `stopImmediatePropagation` so sibling-listener leaks (a focused
 * combobox's own Escape handler, etc.) don't double-handle the key.
 *
 * Module-level state is deliberate: a React context can't be queried
 * from outside the tree, and we need ONE keydown listener that knows
 * about every open dialog regardless of where each lives in the
 * React tree.
 */
const dialogEscStack: Array<() => void> = []
let dialogEscListenerInstalled = false

function ensureDialogEscListener() {
  if (dialogEscListenerInstalled) return
  if (typeof window === 'undefined') return
  dialogEscListenerInstalled = true
  window.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return
    const top = dialogEscStack[dialogEscStack.length - 1]
    if (!top) return
    e.stopImmediatePropagation()
    e.stopPropagation()
    top()
  })
}

interface DialogProps {
  open?: boolean
  onOpenChange?: (open: boolean) => void
  /** Close when the user presses ESC (#1216). Default true; mirrors
   * the sibling Drawer primitive's seam (#1184). A future form with
   * an unsaved-changes guard can pass `false` and run its own dirty
   * check before invoking the close handler. */
  closeOnEscape?: boolean
  children: React.ReactNode
}

function Dialog({ open = false, onOpenChange, closeOnEscape = true, children }: DialogProps) {
  // Push this dialog's close handler onto the global ESC stack while
  // open; pop on close / unmount. The single shared keydown listener
  // (installed lazily on first mount) invokes only the top of the
  // stack, so stacked dialogs close TOP-MOST FIRST rather than all at
  // once. The Cancel button (or whatever close action the dialog body
  // renders) conveys closability sufficiently — no visible "Esc to
  // close" hint is rendered anywhere, per the #1216 design call.
  React.useEffect(() => {
    if (!open || !closeOnEscape) return
    ensureDialogEscListener()
    const handler = () => onOpenChange?.(false)
    dialogEscStack.push(handler)
    return () => {
      const idx = dialogEscStack.lastIndexOf(handler)
      if (idx !== -1) dialogEscStack.splice(idx, 1)
    }
  }, [open, closeOnEscape, onOpenChange])

  const titleId = React.useId()
  return (
    <DialogContext.Provider value={{ open, onOpenChange: onOpenChange ?? (() => {}), titleId }}>
      {children}
    </DialogContext.Provider>
  )
}

function DialogTrigger({ children, asChild }: { children: React.ReactNode; asChild?: boolean }) {
  const context = React.useContext(DialogContext)
  if (!context) throw new Error('DialogTrigger must be used within Dialog')

  if (asChild && React.isValidElement(children)) {
    return React.cloneElement(children as React.ReactElement<{ onClick?: () => void }>, {
      onClick: () => context.onOpenChange(true),
    })
  }

  return (
    <button onClick={() => context.onOpenChange(true)}>
      {children}
    </button>
  )
}

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

/** While a dialog is open: move focus into it (unless a field inside
 * already took it with autoFocus), keep Tab and Shift+Tab inside it, and
 * give focus back to where it was on close. Without this, a keyboard or
 * screen-reader user stays on the page hidden behind the overlay, which
 * matters most for a dialog that opens without a click. */
function useDialogFocus(panel: React.RefObject<HTMLDivElement | null>, open: boolean) {
  React.useEffect(() => {
    if (!open) return
    const before = document.activeElement as HTMLElement | null
    const el = panel.current
    if (el && !el.contains(document.activeElement)) el.focus()
    return () => { if (before && document.contains(before)) before.focus() }
  }, [open, panel])
}

function trapTab(e: React.KeyboardEvent<HTMLDivElement>) {
  if (e.key !== 'Tab') return
  const items = Array.from(e.currentTarget.querySelectorAll<HTMLElement>(FOCUSABLE))
  if (items.length === 0) { e.preventDefault(); return }
  const first = items[0]!
  const last = items[items.length - 1]!
  const active = document.activeElement
  if (e.shiftKey && (active === first || active === e.currentTarget)) {
    e.preventDefault()
    last.focus()
  } else if (!e.shiftKey && active === last) {
    e.preventDefault()
    first.focus()
  }
}

function DialogContent({ children, className }: { children: React.ReactNode; className?: string }) {
  const context = React.useContext(DialogContext)
  if (!context) throw new Error('DialogContent must be used within Dialog')
  const panel = React.useRef<HTMLDivElement>(null)
  useDialogFocus(panel, context.open)

  if (!context.open) return null
  // `createPortal` needs a real DOM node — bail in non-DOM environments
  // (SSR / prerender / node-based renders) rather than throwing. The
  // client-only SPA always has `document` at render time, so this is a
  // safety net, not a hot path.
  if (typeof document === 'undefined') return null

  // Portal to <body> so the modal escapes its mount point in the React
  // tree. `position: fixed` resolves against the nearest ancestor with
  // a `transform`, `filter`, or `backdrop-filter` — not the viewport.
  // The app header sets `backdrop-blur`, so a Dialog rendered inside it
  // (e.g. the sign-out confirm) had its `fixed inset-0` clip to the
  // 56px header strip. Portaling fixes it for every Dialog regardless
  // of where it is mounted.
  return createPortal(
    <div className="fixed inset-0 z-50">
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-xs"
        onClick={() => context.onOpenChange(false)}
      />
      {/* Content */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          ref={panel}
          role="dialog"
          aria-modal="true"
          aria-labelledby={context.titleId}
          tabIndex={-1}
          onKeyDown={trapTab}
          className={cn(
            'outline-hidden',
            // `relative` so the absolutely-positioned close button below
            // anchors to this panel, not the full-screen flex wrapper.
            'relative flex flex-col w-full max-w-lg max-h-[90vh] rounded-lg border bg-background shadow-lg',
            'animate-in fade-in-0 zoom-in-95',
            className
          )}
          onClick={(e) => e.stopPropagation()}
        >
          {children}
          <button
            onClick={() => context.onOpenChange(false)}
            className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-2"
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}

function DialogHeader({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('flex flex-col space-y-1.5 text-center sm:text-left px-6 pt-6 shrink-0', className)}
      {...props}
    />
  )
}

function DialogFooter({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2 px-6 pb-6 pt-4 shrink-0 border-t', className)}
      {...props}
    />
  )
}

function DialogBody({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('flex-1 overflow-y-auto px-6 py-4', className)}
      {...props}
    />
  )
}

function DialogTitle({ className, ...props }: React.HTMLAttributes<HTMLHeadingElement>) {
  const context = React.useContext(DialogContext)
  return (
    <h2
      id={context?.titleId}
      className={cn('text-lg font-semibold leading-none tracking-tight', className)}
      {...props}
    />
  )
}

function DialogDescription({ className, ...props }: React.HTMLAttributes<HTMLParagraphElement>) {
  return (
    <p
      className={cn('text-sm text-muted-foreground', className)}
      {...props}
    />
  )
}

export {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
